import React, { useState, useEffect } from 'react';
import { FileText, User, CheckCircle, XCircle, Building, Building2, LogOut, Eye, Search, Award, Users, TrendingUp, X } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AuthentiChainAdmin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentTab, setCurrentTab] = useState('pending');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvedOrgs, setApprovedOrgs] = useState([]);
  const [rejectedOrgs, setRejectedOrgs] = useState([]);
  
  const [stats, setStats] = useState({
    totalCertificates: 0,
    totalStudents: 0,
    totalOrganizations: 0,
    pendingApprovals: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setAuthToken(token);
      setIsLoggedIn(true);
      fetchAllData(token);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && authToken) {
      fetchAllData(authToken);
    }
  }, [isLoggedIn, authToken]);

  const fetchAllData = async (token) => {
    await Promise.all([
      fetchPendingApprovals(token),
      fetchApprovedOrganizations(token),
      fetchStatistics(token)
    ]);
  };

  const fetchPendingApprovals = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/pending-approvals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  const fetchApprovedOrganizations = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/approved-organizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApprovedOrgs(data);
      }
    } catch (error) {
      console.error('Error fetching approved organizations:', error);
    }
  };

  const fetchRejectedOrganizations = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/rejected-organizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRejectedOrgs(data);
      }
    } catch (error) {
      console.error('Error fetching rejected organizations:', error);
    }
  };

  const fetchStatistics = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();
      
      if (response.ok && data.token) {
        setAuthToken(data.token);
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminData', JSON.stringify(data.admin));
        setIsLoggedIn(true);
        fetchAllData(data.token);
      } else {
        alert(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (id, approve) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/approve/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ approve })
      });

      if (response.ok) {
        alert(approve ? 'Organization approved successfully!' : 'Organization rejected');
        await fetchAllData(authToken);
      } else {
        const data = await response.json();
        alert(data.error || 'Operation failed');
      }
    } catch (error) {
      console.error('Approval error:', error);
      alert('Operation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAuthToken(null);
    setLoginData({ email: '', password: '' });
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'school': return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
      case 'college': return 'bg-purple-500/20 text-purple-300 border-purple-500/50';
      case 'company': return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'school': return <Building className="w-5 h-5" />;
      case 'college': return <Building className="w-5 h-5" />;
      case 'company': return <Building2 className="w-5 h-5" />;
      default: return <Building className="w-5 h-5" />;
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img 
              src="https://raw.githubusercontent.com/King11919/D-verification/refs/heads/main/Authentichain%20logo.png" 
              alt="AuthentiChain Logo" 
              className="h-24 w-auto mx-auto mb-4"
            />
            <h1 className="text-4xl font-bold text-white mb-2">
              Admin Panel
            </h1>
            <p className="text-xl text-orange-200">
              AuthentiChain Management
            </p>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Administrator Login</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Admin Email</label>
                <input
                  type="email"
                  placeholder="admin@authentichain.live"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" data-form-type="other"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <input
                  type="password"
                  placeholder="Enter admin password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" data-form-type="other"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                />
              </div>

              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-lg font-semibold transition-all disabled:bg-gray-500"
              >
                {loading ? 'Logging in...' : 'Login to Admin Panel'}
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-sm text-gray-400 text-center">
                Authorized personnel only. All activities are logged and monitored.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-800/50 backdrop-blur-md border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://raw.githubusercontent.com/King11919/D-verification/refs/heads/main/Authentichain%20logo.png" 
                alt="Logo" 
                className="h-10 w-auto"
              />
              <div>
                <span className="text-xl font-bold text-white block">AuthentiChain</span>
                <span className="text-xs text-orange-300">Admin Panel</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden md:block text-right">
                <p className="text-sm text-white font-semibold">{loginData.email}</p>
                <p className="text-xs text-gray-400">Administrator</p>
              </div>
              
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Statistics Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-6 rounded-xl border border-blue-500/30 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-8 h-8 text-blue-400" />
              <span className="text-xs text-blue-300 font-semibold">TOTAL</span>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{stats.totalCertificates}</p>
            <p className="text-sm text-blue-200">Certificates Issued</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 p-6 rounded-xl border border-purple-500/30 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-purple-400" />
              <span className="text-xs text-purple-300 font-semibold">ACTIVE</span>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{stats.totalStudents}</p>
            <p className="text-sm text-purple-200">Students</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 p-6 rounded-xl border border-emerald-500/30 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-2">
              <Building className="w-8 h-8 text-emerald-400" />
              <span className="text-xs text-emerald-300 font-semibold">APPROVED</span>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{stats.totalOrganizations}</p>
            <p className="text-sm text-emerald-200">Organizations</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 p-6 rounded-xl border border-orange-500/30 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-orange-400" />
              <span className="text-xs text-orange-300 font-semibold">PENDING</span>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{pendingApprovals.length}</p>
            <p className="text-sm text-orange-200">Approvals</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-t-2xl border border-white/10 border-b-0">
          <div className="flex gap-4 px-6 pt-6">
            <button
              onClick={() => setCurrentTab('pending')}
              className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                currentTab === 'pending'
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-700/50 text-gray-400 hover:text-white'
              }`}
            >
              Pending Requests ({pendingApprovals.length})
            </button>
            <button
              onClick={() => {
                setCurrentTab('approved');
                fetchApprovedOrganizations(authToken);
              }}
              className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                currentTab === 'approved'
                  ? 'bg-green-500 text-white'
                  : 'bg-slate-700/50 text-gray-400 hover:text-white'
              }`}
            >
              Approved ({approvedOrgs.length})
            </button>
            <button
              onClick={() => {
                setCurrentTab('rejected');
                fetchRejectedOrganizations(authToken);
              }}
              className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                currentTab === 'rejected'
                  ? 'bg-red-500 text-white'
                  : 'bg-slate-700/50 text-gray-400 hover:text-white'
              }`}
            >
              Rejected ({rejectedOrgs.length})
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-b-2xl border border-white/10 p-6">
          {/* Pending Requests */}
          {currentTab === 'pending' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">Pending Registration Requests</h3>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Search className="w-4 h-4" />
                  <span>{pendingApprovals.length} requests awaiting review</span>
                </div>
              </div>

              {pendingApprovals.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-xl font-semibold mb-2">All Caught Up!</p>
                  <p>No pending requests at the moment</p>
                </div>
              ) : (
                pendingApprovals.map(request => (
                  <div key={request._id} className="bg-slate-700/50 rounded-xl p-6 border border-slate-600 hover:border-orange-500/50 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`px-3 py-1 rounded-lg text-xs font-semibold border flex items-center gap-1 ${getTypeColor(request.type)}`}>
                            {getTypeIcon(request.type)}
                            {request.type.toUpperCase()}
                          </span>
                          <span className="text-gray-400 text-sm">
                            Submitted: {new Date(request.submittedDate).toLocaleDateString()}
                          </span>
                        </div>
                        <h4 className="text-2xl font-bold text-white mb-2">{request.organizationName}</h4>
                        <p className="text-blue-300 font-semibold mb-4">{request.email}</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-600">
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Contact Person</p>
                        <p className="text-white font-semibold">{request.contactPerson}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Phone</p>
                        <p className="text-white font-semibold">{request.phone}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-400 mb-1">Address</p>
                        <p className="text-white font-semibold">{request.address}</p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApproval(request._id, true)}
                        disabled={loading}
                        className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:bg-gray-500"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Approve Organization
                      </button>
                      <button
                        onClick={() => handleApproval(request._id, false)}
                        disabled={loading}
                        className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:bg-gray-500"
                      >
                        <XCircle className="w-5 h-5" />
                        Reject Request
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Approved Organizations */}
          {currentTab === 'approved' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Approved Organizations</h3>
                <span className="text-sm text-green-300 font-semibold">{approvedOrgs.length} Active</span>
              </div>

              {approvedOrgs.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Building className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No approved organizations yet</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {approvedOrgs.map(org => (
                    <div key={org._id} className="bg-slate-700/50 rounded-xl p-6 border border-green-500/30 hover:border-green-500/50 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className={`px-2 py-1 rounded text-xs font-semibold border ${getTypeColor(org.type)}`}>
                            {org.type.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400">
                          Approved: {new Date(org.approvedDate).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <h4 className="text-xl font-bold text-white mb-2">{org.organizationName}</h4>
                      <p className="text-sm text-gray-400 mb-1">{org.email}</p>
                      <p className="text-sm text-gray-400 mb-3">{org.contactPerson}</p>
                      
                      <button className="w-full py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 text-blue-300 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2">
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Rejected Organizations */}
          {currentTab === 'rejected' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Rejected Requests</h3>
                <span className="text-sm text-red-300 font-semibold">{rejectedOrgs.length} Rejected</span>
              </div>

              {rejectedOrgs.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <XCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No rejected requests</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {rejectedOrgs.map(org => (
                    <div key={org._id} className="bg-slate-700/50 rounded-xl p-6 border border-red-500/30 hover:border-red-500/50 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <XCircle className="w-5 h-5 text-red-400" />
                          <span className={`px-2 py-1 rounded text-xs font-semibold border ${getTypeColor(org.type)}`}>
                            {org.type.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400">
                          Rejected: {new Date(org.rejectedDate).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <h4 className="text-xl font-bold text-white mb-2">{org.organizationName}</h4>
                      <p className="text-sm text-gray-400 mb-1">{org.email}</p>
                      <p className="text-sm text-gray-400">{org.contactPerson}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AuthentiChainAdmin;
